import colors from '@lmc/common/constants'
import type { Resident } from '@lmc/ts-common'
import { Avatar, Button, EmptyState, Heading, NoDataIcon } from '@lmc/ui-kit'
import DropdownIcon from '@lmc/ui-kit/src/dataDisplay/ListItem/DropdownIcon'
import React, { useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { getRouteWithParams } from '../../../../../components/routes/utils'
import { NAMESPACE } from '../../../../../locale'
import { goalReportsConstants } from '../../../../../locale/goalReports'
import { commonPatterns } from '../../../../../utils/enhancedTestIds'
import type {
  GoalReportListType,
  GoalSummary
} from '../../../utils/goalsManagementTypes'
import GoalReportsRow from './GoalReportRow'
import GoalOverdueIcon from './icons/GoalOverdueIcon'

const GoalReportsSection = ({
  data,
  testId
}: {
  data: GoalReportListType
  testId?: string
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const residents = useSelector(
    (state: { data: { residents: Resident[] } }) => state.data.residents
  )
  const resident: Resident | undefined = residents?.find(
    (res) => res.id === data?.id
  )
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const navigate = useNavigate()

  const navigateToGoal = (goalID: string) => {
    const route = getRouteWithParams('/residents/:id/goals/:goalID', {
      goalID,
      id: resident?.id
    })

    navigate(route, { state: { referrer: 'management' } })
  }
  const navigateToGoalsDashboard = () => {
    const route = getRouteWithParams('/residents/:id/goals', {
      id: resident?.id
    })

    navigate(route)
  }

  const getName = (item: Resident) =>
    `${item?.preferredName || item.name.first} ${item.name.last}`
  const activeGoalsText = `${data?.goalsStats?.activeGoals} goal${
    data?.goalsStats?.activeGoals !== 1 ? 's' : ''
  } `
  const hasZeroGoals = data?.goalsStats?.activeGoals === 0

  return (
    resident && (
      <MainContainer
        data-testid={
          testId ? `${testId}-goal-reports-section` : 'goal-reports-section'
        }
      >
        <HeaderContainer
          className="sectionHeader"
          onClick={() => {
            setIsOpen((prev) => !prev)
          }}
          data-testid={
            testId
              ? `${testId}-goal-reports-section-header`
              : 'goal-reports-section-header'
          }
        >
          <ResidentContainer>
            <Avatar
              url={resident?.picture as unknown as string}
              size={40}
              data-testid={
                testId
                  ? `${testId}-goal-reports-section-avatar`
                  : 'goal-reports-section-avatar'
              }
            />
            <ResidentName level={5} weight="bold">
              {getName(resident)}
            </ResidentName>
          </ResidentContainer>
          <GoalsHeaderContainer>
            <GoalsLabel level={5} hasZeroGoals={hasZeroGoals}>
              {activeGoalsText}
            </GoalsLabel>
            {data?.goalsStats?.overdueGoals > 0 && (
              <GoalsRightContainer
                data-testid={
                  testId
                    ? `${testId}-goal-reports-section-overdue`
                    : 'goal-reports-section-overdue'
                }
              >
                <GoalOverdueIcon />
                <GoalsLabel level={5}>
                  {data?.goalsStats?.overdueGoals}
                </GoalsLabel>
              </GoalsRightContainer>
            )}
          </GoalsHeaderContainer>
          <ToggleContainer>
            <RotatedDropdownIcon isCollapsed={!isOpen} />
          </ToggleContainer>
        </HeaderContainer>
        <AnimateHeight
          animateOpacity
          duration={500}
          height={isOpen ? 'auto' : 0}
        >
          <GoalRowsContainer
            data-testid={
              testId
                ? `${testId}-goal-reports-section-rows`
                : 'goal-reports-section-rows'
            }
          >
            {data?.goals?.length ? (
              data?.goals.map((goal: GoalSummary, index: number) => {
                return (
                  <GoalReportsRow
                    key={goal?.id}
                    goal={goal}
                    onClick={() => navigateToGoal(goal.id)}
                    testId={
                      testId ? commonPatterns.row(testId, index) : undefined
                    }
                  />
                )
              })
            ) : (
              <EmptyState
                title={t(goalReportsConstants.EMPTY_STATE)}
                icon={<NoDataIcon />}
                data-testid={
                  testId
                    ? `${testId}-goal-reports-section-empty`
                    : 'goal-reports-section-empty'
                }
              >
                <ButtonWrapper onClick={navigateToGoalsDashboard}>
                  <Button
                    variant="text"
                    data-testid={
                      testId
                        ? `${testId}-goal-reports-section-add-goal-btn`
                        : 'goal-reports-section-add-goal-btn'
                    }
                  >
                    {t(goalReportsConstants.ADD_A_GOAL_HERE)}
                  </Button>
                </ButtonWrapper>
              </EmptyState>
            )}
          </GoalRowsContainer>
        </AnimateHeight>
      </MainContainer>
    )
  )
}

export default GoalReportsSection

const MainContainer = styled.div`
  background-color: ${colors.white};
  display: flex;
  border-radius: 16px;
  padding: 16px 12px;
  flex-direction: column;
  cursor: pointer;
`

const HeaderContainer = styled.div`
  display: flex;
  gap: 16px;
`

const ResidentContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 16px;
`

const ResidentName = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const GoalsHeaderContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`

const GoalsLabel = styled(Heading)<{ hasZeroGoals?: boolean }>`
  color: ${(p) => (p.hasZeroGoals ? colors.silver : colors.black)};
`

const GoalsRightContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

const ToggleContainer = styled.div`
  align-items: center;
  display: flex;
`

const GoalRowsContainer = styled.div`
  gap: 12px;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`
const RotatedDropdownIcon = styled(DropdownIcon)<{ isCollapsed?: boolean }>`
  transform: ${(props) =>
    props.isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 0.3s ease-in-out;
`

const ButtonWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
`
